import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/navbar/index.jsx';
import Footer from '../../components/Footer/Footer.js';
import WaFloat from '../../components/WaFloat/WaFloat.jsx';
import { useReveal } from '../../hooks/useReveal.js';

/* ── inline icons ── */
const I = {
  pin: p => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z" stroke="currentColor" strokeWidth="1.6" /><circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.6" /></svg>),
  cal: p => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
  users: p => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 6.2a3 3 0 0 1 0 5.6M20.5 19a5.5 5.5 0 0 0-3.2-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
  layers: p => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="m12 3 9 5-9 5-9-5 9-5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="m3.5 12 8.5 5 8.5-5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>),
  check: p => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="m5 12.5 4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  arrow: p => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
};

const NAV = [
  { id: 'room', label: 'The room' },
  { id: 'programme', label: 'Programme' },
  { id: 'evening', label: 'The evening' },
  { id: 'seats', label: 'Seats' },
  { id: 'apply', label: 'Apply' },
];

const FRONT = [
  ['Venue', 'Taj Lands End', 'Mumbai', 'pin'],
  ['Date', 'Friday, 27 November 2026', 'Registration from 6:30 pm, dinner from 10:25 pm', 'cal'],
  ['Format', 'Four teaching modules and a closing panel', 'No parallel tracks. No sponsor presentations.', 'layers'],
  ['Room', 'Up to 200 promoters, directors and CFOs', 'Company side only. Every application is screened.', 'users'],
];

const CRITERIA = [
  ['Designation', 'Promoter, Chairman, Managing Director, Whole-time Director or CFO'],
  ['Revenue', '₹50 crore to ₹250 crore'],
  ['Profitability', 'Profitable, or profitable within the last two financial years'],
  ['Intent', 'Listing under active consideration within 12 to 36 months'],
  ['Entity', 'Private limited or unlisted public limited'],
  ['Region', 'Mumbai, Thane, Navi Mumbai, Pune, and the Gujarat and Rajasthan corridor'],
];

const MODULES = [
  ['01', 'Should you list, and what it costs', 'SME platforms against the mainboard against staying private. Issue expense, market making, and the compliance load that never goes away afterwards. Two honest cases where listing did not work.', '35 minutes'],
  ['02', 'The twenty-four month clean-up', 'Eligibility and track record, promoter holding, unwinding related-party transactions, statutory dues, restated financials, changing auditor, and consolidating the group. What to do first, and what cannot be left until the last year.', '40 minutes'],
  ['03', "The banker's view", 'What causes a merchant banker to decline a mandate. Realistic timelines from first meeting to listing, how the pre-IPO round is priced, and what dilution actually looks like once the maths is done.', '35 minutes'],
  ['04', "A listed promoter's journey", 'A promoter who listed recently on their real timeline, real costs, what surprised them, and what they would do differently. Unedited.', '20 minutes'],
  ['05', 'Panel and open questions', 'Full faculty on stage, moderated. Bring the question you have not been able to get a straight answer to.', '15 minutes'],
];

const SCHEDULE = [
  ['5:30 pm', 'Registration and networking', 'Tea, coffee and soft beverages', false],
  ['6:10 pm', 'Welcome', '', false],
  ['6:30 pm', 'Module 1 — Should you list, and what it costs', '', false],
  ['7:10 pm', 'Module 2 — The twenty-four month clean-up', '', false],
  
  ['8:00 pm', "Module 3 — The banker's view", '', false],
  ['8:45 pm', "Module 4 — A listed promoter's journey", '', false],
  ['9:15 pm', 'Panel and open questions', '', false],
  ['9:45 pm', 'Recognition ceremony', 'Certificates presented on stage to companies meeting the recognition criteria', true],
  ['10:15 pm', 'Dinner and networking', 'Faculty seated across the tables, not at a head table', true],
];

const SEATS = [
  { name: 'Delegate', price: '₹15,000', cap: 'Per person', feature: false, points: ['All four modules and the closing panel', 'Dinner and networking with the faculty', 'A written readiness diagnostic for your company, prepared after the event', 'Certificate of participation by email'] },
  { name: 'Premium delegate', price: '₹28,000', cap: 'Per person · Thirty seats only', feature: true, points: ['Everything in the delegate seat', 'Reserved seating and a reserved table at dinner, with faculty seated alongside', 'Recognition on stage and your certificate presented in person', 'A professional photograph, delivered within 48 hours', 'Priority slot for your readiness diagnostic'] },
];

const REQUIRED = ['name', 'role', 'company', 'email', 'phone', 'revenue', 'timeline', 'entity', 'seat'];

export default function ListingRoom() {
  useReveal();
  const [active, setActive] = useState('room');
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ name: '', role: '', company: '', email: '', phone: '', revenue: '', timeline: '', entity: '', seat: '', notes: '', consent: false });
  const [status, setStatus] = useState(null); // {type:'ok'|'err', msg}
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: '-45% 0px -50% 0px' }
    );
    NAV.forEach(n => { const el = document.getElementById(n.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const de = document.documentElement;
        const max = (de.scrollHeight - window.innerHeight) || 1;
        setProgress(Math.min(100, Math.max(0, (window.scrollY / max) * 100)));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED.filter(k => !String(form[k]).trim());
    if (missing.length) {
      setStatus({ type: 'err', msg: 'Complete the highlighted fields so we can assess the application.' });
      const el = document.getElementById('lr-' + missing[0]);
      if (el) el.focus();
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwACF4KncuEgQ0nQbC_M5dGqUvWEDyiUNWfyIzDWN6v44elu3UyLMUeFM2NjuzTYQU5eQ/exec';
      const EXTERNAL_API_URL = 'https://admin.connect195.com/api/public/collect/cmpc9wplz003hksckkaakojsq';
      const enquiry = 'The Listing Room — Seat application';
      const context = [
        `Designation: ${form.role}`, `Revenue: ${form.revenue}`, `Timeline: ${form.timeline}`,
        `Entity: ${form.entity}`, `Seat: ${form.seat}`,
        form.notes && `Notes: ${form.notes}`,
        `Partner-share consent: ${form.consent ? 'Yes' : 'No'}`,
      ].filter(Boolean).join(' | ');

      const sheets = fetch(GOOGLE_SHEETS_URL, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, enquiry, context }) });
      const api = fetch(EXTERNAL_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, company: form.company, enquiry, context }) });
      const results = await Promise.allSettled([sheets, api]);
      if (results.some(r => r.status === 'fulfilled')) {
        setDone(true);
        setStatus({ type: 'ok', msg: 'Application received. We will confirm within two working days.' });
      } else { throw new Error('failed'); }
    } catch (err) {
      console.error('Submission error:', err);
      setStatus({ type: 'err', msg: 'Something went wrong. Please try again, or email us directly.' });
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="lr-progress" style={{ width: `${progress}%` }} />
      <Navbar />

      {/* ── COVER ── */}
      <section className="lr-cover">
        <div className="lr-cover-glow" aria-hidden="true" />
        <div className="lr-wrap lr-cover-grid">
          <div className="lr-cover-main">
            <p className="lr-kicker">AQT Direct Limited, with Indus Experts</p>
            <h1 className="lr-title">The<em>Listing Room</em></h1>
            <div className="lr-draw" />
            <p className="lr-standfirst">Your company may be ready to grow. The question this evening answers is whether it is ready to <em>list</em> — and what has to change in the two years before it can.</p>
            <div className="lr-cta-row">
              <a href="#apply" className="btn-white lr-btn-arrow">Apply for a seat <I.arrow className="lr-btn-ic" /></a>
              <a href="#programme" className="btn-white-outline">Read the programme</a>
            </div>
            <p className="lr-cover-note">Seats are confirmed after screening, not on payment.</p>
          </div>

          <aside className="lr-frontmatter">
            {FRONT.map(([dt, dd, sm, ic]) => {
              const Ico = I[ic];
              return (
                <div className="lr-fm-row" key={dt}>
                  <span className="lr-fm-ic"><Ico /></span>
                  <div>
                    <div className="lr-fm-dt">{dt}</div>
                    <div className="lr-fm-dd">{dd}</div>
                    <div className="lr-fm-sm">{sm}</div>
                  </div>
                </div>
              );
            })}
          </aside>
        </div>
      </section>

      {/* ── STICKY SUB-NAV ── */}
      <nav className="lr-subnav">
        <div className="lr-subnav-inner">
          <span className="lr-subnav-brand">The Listing Room</span>
          <div className="lr-subnav-links">
            {NAV.map(n => <a key={n.id} href={`#${n.id}`} className={`lr-subnav-link${active === n.id ? ' is-active' : ''}`}>{n.label}</a>)}
          </div>
          <a href="#apply" className="btn-primary lr-subnav-cta">Apply for a seat</a>
        </div>
      </nav>

      {/* ── 01 · WHY ── */}
      <section id="why" className="lr-sec">
        <div className="lr-wrap">
          <header className="lr-sechead reveal"><span className="lr-num">01</span><h2>Most companies that fail to list were never told what was wrong.</h2></header>
          <div className="lr-two">
            <div className="lr-measure reveal reveal-delay-1">
              <p>A listing is decided long before the draft prospectus is filed. It is decided in how the group is structured, how revenue is recognised, what sits in related-party transactions, and whether the last three years of financials can survive restatement.</p>
              <p>By the time a merchant banker declines a mandate, most of those things are expensive to fix — and some cannot be fixed in time at all.</p>
            </div>
            <div className="lr-measure reveal reveal-delay-2">
              <p>This evening is the conversation that usually happens too late: what the process actually costs, what causes a banker to say no, what has to be cleaned up and in what order, and whether listing is the right route for your company at all.</p>
              <p>It is taught by people who do this work, to a room of promoters at the same stage. <em>Nobody is selling from the stage.</em></p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 02 · ROOM ── */}
      <section id="room" className="lr-sec lr-sec-off">
        <div className="lr-wrap">
          <header className="lr-sechead reveal"><span className="lr-num">02</span><h2>Who is in the room</h2></header>
          <div className="lr-two lr-two-wide">
            <div className="reveal reveal-delay-1">
              <dl className="lr-criteria">
                {CRITERIA.map(([k, v]) => (
                  <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
                ))}
              </dl>
            </div>
            <div className="lr-measure reveal reveal-delay-2">
              <p>Every application is screened on revenue, profitability, entity structure and listing timeline before a seat is confirmed. Applications outside the profile are declined — and we say so.</p>
              <p>Investors, distributors and intermediaries seeking investors are not admitted. This is a company-side room, and the screening is what makes it worth the evening.</p>
              <p className="lr-fine">Faculty is announced as each speaker is confirmed. Every session is taught: no company slides, no credentials decks, no case studies of the speaker's own mandates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 03 · PROGRAMME ── */}
      <section id="programme" className="lr-sec">
        <div className="lr-wrap">
          <header className="lr-sechead reveal"><span className="lr-num">03</span><h2>Four modules, in the order the decisions actually arrive.</h2></header>
          <div className="lr-modules">
            {MODULES.map(([n, title, body, len], i) => (
              <article className={`lr-module${n === '05' ? ' lr-module-wide' : ''} reveal reveal-delay-${(i % 3) + 1}`} key={n}>
                <div className="lr-module-n">{n}</div>
                <div className="lr-module-body">
                  <h3>{title}</h3>
                  <p>{body}</p>
                  <span className="lr-module-len">{len}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 04 · EVENING ── */}
      <section id="evening" className="lr-sec lr-evening">
        <div className="lr-evening-glow" aria-hidden="true" />
        <div className="lr-wrap">
          <header className="lr-sechead reveal"><span className="lr-num">04</span><h2>One evening, start to finish.</h2></header>
          <div className="lr-schedule">
            {SCHEDULE.map(([t, label, sub, mark], i) => (
              <div className={`lr-slot${mark ? ' is-mark' : ''} reveal`} key={t + i}>
                <time>{t}</time>
                <div className="lr-slot-body">
                  <p>{label}</p>
                  {sub && <small>{sub}</small>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 05 · SEATS ── */}
      <section id="seats" className="lr-sec lr-sec-off">
        <div className="lr-wrap">
          <header className="lr-sechead reveal"><span className="lr-num">05</span><h2>Two ways to take a seat.</h2></header>
          <div className="lr-seats">
            {SEATS.map(s => (
              <div className={`lr-seat${s.feature ? ' is-feat' : ''} reveal`} key={s.name}>
                {s.feature && <span className="lr-seat-badge">Thirty seats</span>}
                <h3>{s.name}</h3>
                <div className="lr-seat-price">{s.price}<sup>plus GST</sup></div>
                <div className="lr-seat-cap">{s.cap}</div>
                <ul className="lr-seat-list">
                  {s.points.map(p => <li key={p}><span className="lr-seat-check"><I.check /></span>{p}</li>)}
                </ul>
                <a href="#apply" className={s.feature ? 'btn-primary lr-seat-btn lr-btn-arrow' : 'btn-outline lr-seat-btn lr-btn-arrow'}>Apply for this seat <I.arrow className="lr-btn-ic" /></a>
              </div>
            ))}
          </div>
          <p className="lr-fine lr-fine-center">Recognition is assessed against stated criteria: revenue, three-year growth, years in operation and listing intent. Applications outside the profile are moved to the delegate seat rather than recognised.</p>
        </div>
      </section>

      {/* ── 06 · APPLY ── */}
      <section id="apply" className="lr-sec">
        <div className="lr-wrap">
          <header className="lr-sechead reveal"><span className="lr-num">06</span><h2>Apply for a seat.</h2></header>
          <div className="lr-apply">
            <div className="lr-measure reveal reveal-delay-1">
              <p>This is an application, not a checkout. Tell us about the company and we will confirm within two working days whether there is a seat — and send payment details if there is.</p>
              <p>If your company is outside the profile we will say so plainly rather than take the money. That rule is the only reason the room is worth attending.</p>
              <div className="lr-contact-card">
                <div className="lr-contact-card-label">Contact Us</div>
                <div className="lr-contact-tags">
                  <span className="lr-contact-tag">Speaking Opportunity</span>
                  <span className="lr-contact-tag">Partnership</span>
                  <span className="lr-contact-tag">Delegates</span>
                  <span className="lr-contact-tag">Collabs</span>
                </div>
                <div className="lr-contact-card-inner">
                  <div className="lr-cc-row"><div className="lr-cc-k">Name</div><div className="lr-cc-v">Abhishek Singh</div></div>
                  <div className="lr-cc-row"><div className="lr-cc-k">Questions before you apply</div><div className="lr-cc-v"><a href="mailto:support@aqtdirect.com">support@aqtdirect.com</a></div></div>
                  <div className="lr-cc-row"><div className="lr-cc-k">Phone / WhatsApp</div><div className="lr-cc-v"><a href="https://api.whatsapp.com/send?phone=919667773258" target="_blank" rel="noopener noreferrer">+91 96677 73258</a></div></div>
                </div>
              </div>
            </div>

            <div className="lr-form-card reveal reveal-delay-2">
              <div className="lr-form-accent" aria-hidden="true" />
              <form onSubmit={handleSubmit} noValidate>
                <div className="lr-form-2">
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-name">Your name</label><input id="lr-name" className="cf-input" type="text" name="name" autoComplete="name" value={form.name} onChange={handleChange} /></div>
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-role">Designation</label>
                    <select id="lr-role" className="cf-select" name="role" value={form.role} onChange={handleChange}>
                      <option value="">Select</option><option>Promoter / Chairman</option><option>Managing Director</option><option>Whole-time Director</option><option>Chief Financial Officer</option><option>Other senior management</option>
                    </select>
                  </div>
                </div>
                <div className="cf-field"><label className="cf-label" htmlFor="lr-company">Company</label><input id="lr-company" className="cf-input" type="text" name="company" autoComplete="organization" value={form.company} onChange={handleChange} /></div>
                <div className="lr-form-2">
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-email">Work email</label><input id="lr-email" className="cf-input" type="email" name="email" autoComplete="email" value={form.email} onChange={handleChange} /></div>
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-phone">Mobile</label><input id="lr-phone" className="cf-input" type="tel" name="phone" autoComplete="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/[^0-9+\s\-()]/g, '') }))} inputMode="numeric" /></div>
                </div>
                <div className="lr-form-2">
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-revenue">Last full-year revenue</label>
                    <select id="lr-revenue" className="cf-select" name="revenue" value={form.revenue} onChange={handleChange}>
                      <option value="">Select</option><option>Under ₹50 crore</option><option>₹50–100 crore</option><option>₹100–250 crore</option><option>Above ₹250 crore</option>
                    </select>
                  </div>
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-timeline">Listing timeline</label>
                    <select id="lr-timeline" className="cf-select" name="timeline" value={form.timeline} onChange={handleChange}>
                      <option value="">Select</option><option>Within 12 months</option><option>12–24 months</option><option>24–36 months</option><option>Exploring, no timeline yet</option>
                    </select>
                  </div>
                </div>
                <div className="lr-form-2">
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-entity">Entity type</label>
                    <select id="lr-entity" className="cf-select" name="entity" value={form.entity} onChange={handleChange}>
                      <option value="">Select</option><option>Private limited</option><option>Unlisted public limited</option><option>Other</option>
                    </select>
                  </div>
                  <div className="cf-field"><label className="cf-label" htmlFor="lr-seat">Seat</label>
                    <select id="lr-seat" className="cf-select" name="seat" value={form.seat} onChange={handleChange}>
                      <option value="">Select</option><option>Delegate — ₹15,000 + GST</option><option>Premium delegate — ₹28,000 + GST</option><option>Not sure yet</option>
                    </select>
                  </div>
                </div>
                <div className="cf-field"><label className="cf-label" htmlFor="lr-notes">Anything we should know (optional)</label><textarea id="lr-notes" className="cf-textarea" name="notes" rows="3" value={form.notes} onChange={handleChange} /></div>

                <label className="lr-consent"><input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} /><span>I agree that my name, company and designation may be shared with the event's partners. Optional — leaving this unticked does not affect my application.</span></label>

                {status && <div className={`lr-status ${status.type === 'ok' ? 'is-ok' : 'is-err'}`}>{status.msg}</div>}

                <button type="submit" className="btn-primary lr-btn-arrow" disabled={loading || done} style={{ padding: '14px 30px', opacity: (loading || done) ? 0.7 : 1, cursor: (loading || done) ? 'not-allowed' : 'pointer' }}>
                  {done ? 'Application received' : loading ? 'Submitting…' : <>Submit application <I.arrow className="lr-btn-ic" /></>}
                </button>
                <p className="lr-formnote">By applying you agree to be contacted about this event. Your details are used to assess the application and are not sold.</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── COLOPHON ── */}
      <section className="lr-colophon">
        <div className="lr-wrap">
          <div className="lr-colo-grid">
            <div><strong>The Listing Room</strong>Capital Readiness Masterclass for Promoters<br />Taj Lands End, Mumbai, Friday, 27 November 2026</div>
            <div><strong>Hosted by</strong>AQT Direct Limited<br />Corporate and transaction advisory</div>
            <div><strong>Knowledge Partner</strong>Indus Experts<br /><a href="mailto:support@aqtdirect.com">support@aqtdirect.com</a></div>
          </div>
          <p className="lr-disclaimer">AQT Direct Limited is a corporate and transaction advisory firm. The Listing Room is an educational programme for company promoters and management. It does not constitute investment advice, and no securities are offered or recommended at the event or in any associated material. The audience is company side only; investors, distributors and intermediaries seeking investors are not admitted.</p>
        </div>
      </section>

      <Footer />
      <WaFloat />

      <style>{`
        .lr-progress{position:fixed;top:0;left:0;height:3px;background:var(--grad);z-index:400;transition:width .1s linear;box-shadow:0 0 10px rgba(29,184,138,.6)}
        .lr-wrap{max-width:1320px;margin:0 auto;padding:0 clamp(24px,5vw,72px)}
        .lr-measure{max-width:38em}
        .lr-btn-arrow{display:inline-flex!important;align-items:center;gap:8px}
        .lr-btn-ic{width:17px;height:17px;transition:transform .25s}
        .lr-btn-arrow:hover .lr-btn-ic{transform:translateX(4px)}

        /* COVER */
        .lr-cover{position:relative;overflow:hidden;background:radial-gradient(125% 105% at 82% 0%,#173463 0%,var(--blue-dark) 55%);color:#fff;padding:150px 0 96px;scroll-margin-top:120px}
        .lr-cover-glow{position:absolute;top:-25%;right:-6%;width:56%;height:130%;background:radial-gradient(closest-side,rgba(29,184,138,.2),transparent 70%);filter:blur(14px);z-index:0}
        .lr-cover-grid{position:relative;z-index:1;display:grid;grid-template-columns:1.4fr 1fr;gap:clamp(2rem,5vw,4.5rem);align-items:center}
        .lr-kicker{font-family:'DM Sans',sans-serif;font-size:12px;font-weight:500;letter-spacing:.16em;text-transform:uppercase;color:var(--teal-light);margin:0 0 26px}
        .lr-title{font-family:'Cormorant Garamond',serif;font-size:clamp(48px,7.2vw,102px);font-weight:500;line-height:.94;letter-spacing:-.025em;margin:0 0 26px;color:#fff}
        .lr-title em{display:block;font-style:italic;font-weight:600;line-height:1.06;padding-bottom:.1em;background:linear-gradient(120deg,#2DD4A0,#8fd4ff);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        .lr-draw{height:2px;width:100%;background:var(--grad);transform-origin:left;animation:lrDraw 1.1s cubic-bezier(.2,.7,.3,1) both;margin-bottom:28px;border-radius:2px}
        @keyframes lrDraw{from{transform:scaleX(0)}to{transform:scaleX(1)}}
        .lr-standfirst{font-size:clamp(18px,2.2vw,25px);line-height:1.5;color:rgba(255,255,255,.82);font-weight:300;max-width:20em;margin:0}
        .lr-standfirst em{font-style:italic;color:var(--teal-light)}
        .lr-cta-row{display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin-top:34px}
        .lr-cover-note{font-family:'DM Sans',sans-serif;font-size:13px;color:rgba(255,255,255,.5);margin:18px 0 0}
        .lr-frontmatter{display:flex;flex-direction:column;gap:2px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.13);border-radius:16px;padding:10px 26px;backdrop-filter:blur(6px)}
        .lr-fm-row{display:flex;gap:15px;align-items:flex-start;padding:18px 0;border-bottom:1px solid rgba(255,255,255,.1)}
        .lr-fm-row:last-child{border-bottom:none}
        .lr-fm-ic{width:38px;height:38px;border-radius:10px;background:rgba(29,184,138,.14);border:1px solid rgba(45,212,160,.3);color:var(--teal-light);display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .lr-fm-ic svg{width:20px;height:20px}
        .lr-fm-dt{font-family:'DM Sans',sans-serif;font-size:11px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;color:var(--teal-light);margin-bottom:5px}
        .lr-fm-dd{font-family:'Cormorant Garamond',serif;font-size:18px;font-weight:500;color:#fff;line-height:1.3}
        .lr-fm-sm{font-family:'DM Sans',sans-serif;font-size:12.5px;color:rgba(255,255,255,.5);margin-top:4px;line-height:1.5}

        /* SUB-NAV */
        .lr-subnav{position:sticky;top:72px;z-index:150;background:rgba(255,255,255,.9);backdrop-filter:blur(16px);border-bottom:1px solid var(--border)}
        .lr-subnav-inner{max-width:1320px;margin:0 auto;padding:0 clamp(24px,5vw,72px);display:flex;align-items:center;gap:20px;height:56px}
        .lr-subnav-brand{font-family:'Cormorant Garamond',serif;font-size:18px;font-weight:600;color:var(--blue-dark);white-space:nowrap}
        .lr-subnav-links{display:flex;gap:2px;margin-left:auto;overflow-x:auto;scrollbar-width:none}
        .lr-subnav-links::-webkit-scrollbar{display:none}
        .lr-subnav-link{font-size:13px;color:var(--muted);text-decoration:none;padding:8px 14px;border-radius:7px;white-space:nowrap;transition:color .2s,background .2s;position:relative}
        .lr-subnav-link:hover{color:var(--blue)}
        .lr-subnav-link.is-active{color:var(--blue);background:var(--off);font-weight:500}
        .lr-subnav-link.is-active::after{content:'';position:absolute;left:14px;right:14px;bottom:-1px;height:2px;background:var(--grad);border-radius:2px}
        .lr-subnav-cta{padding:8px 20px;font-size:13px;white-space:nowrap}

        /* SECTION SHELL */
        .lr-sec{padding:clamp(64px,8vw,110px) 0;scroll-margin-top:130px;background:var(--white)}
        .lr-sec-off{background:var(--off)}
        .lr-sechead{display:grid;grid-template-columns:56px 1fr;gap:22px;margin-bottom:clamp(36px,5vw,58px);align-items:start}
        .lr-num{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:600;color:var(--teal);padding-top:8px;position:relative}
        .lr-num::after{content:'';position:absolute;left:0;top:38px;width:28px;height:2px;background:var(--grad);border-radius:2px}
        .lr-sechead h2{font-family:'Cormorant Garamond',serif;font-size:clamp(28px,4vw,48px);font-weight:600;line-height:1.1;letter-spacing:-.015em;color:var(--blue-dark);max-width:16em}
        .lr-two{display:grid;grid-template-columns:1fr 1fr;gap:clamp(28px,4vw,64px)}
        .lr-two-wide{grid-template-columns:1.1fr 1fr}
        .lr-measure p{font-size:16px;color:var(--muted);line-height:1.85;font-weight:300;margin:0 0 18px;max-width:40em}
        .lr-measure p:last-child{margin-bottom:0}
        .lr-measure em{font-style:italic;color:var(--blue-dark);font-weight:400}
        .lr-fine{font-family:'DM Sans',sans-serif;font-size:13.5px;color:var(--muted);line-height:1.7;font-weight:300;padding-left:16px;border-left:2px solid var(--teal);margin-top:24px}
        .lr-fine-center{max-width:760px;margin:34px auto 0;text-align:center;border:none;padding:24px 24px 0;border-top:1px solid var(--border)}

        /* CRITERIA */
        .lr-criteria{margin:0;border-top:1px solid var(--border)}
        .lr-criteria>div{display:grid;grid-template-columns:12rem 1fr;gap:20px;padding:16px 0;border-bottom:1px solid var(--border)}
        .lr-criteria dt{font-family:'DM Sans',sans-serif;font-size:11.5px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:var(--teal-dark)}
        .lr-criteria dd{margin:0;font-size:14.5px;color:var(--muted);line-height:1.6;font-weight:300}
        @media(max-width:640px){.lr-criteria>div{grid-template-columns:1fr;gap:4px}}

        /* MODULES */
        .lr-modules{display:grid;grid-template-columns:1fr 1fr;gap:0 clamp(40px,4.5vw,80px);border-top:1px solid var(--border)}
        .lr-module{display:grid;grid-template-columns:54px 1fr;gap:22px;padding:28px 0;border-bottom:1px solid var(--border);transition:transform .22s}
        .lr-module:hover{transform:translateX(5px)}
        .lr-module.lr-module-wide{grid-column:1 / -1;background:var(--off);border-radius:16px;border-bottom:none;margin-top:24px;padding:30px 34px;align-items:center}
        .lr-module-n{font-family:'Cormorant Garamond',serif;font-size:34px;font-weight:600;color:var(--teal);line-height:1;padding-top:2px}
        .lr-module-body h3{font-family:'Cormorant Garamond',serif;font-size:clamp(21px,2.2vw,26px);font-weight:600;color:var(--blue-dark);margin:0 0 8px;line-height:1.2}
        .lr-module-body p{font-size:14.5px;color:var(--muted);line-height:1.75;font-weight:300;margin:0}
        .lr-module-len{display:inline-block;margin-top:12px;font-family:'DM Sans',sans-serif;font-size:11px;font-weight:500;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);background:var(--off);padding:5px 12px;border-radius:20px}
        .lr-module-wide .lr-module-len{background:#fff}
        @media(max-width:820px){.lr-modules{grid-template-columns:1fr}.lr-module.lr-module-wide{grid-column:auto}}
        @media(max-width:640px){.lr-module{grid-template-columns:1fr;gap:8px}}

        /* EVENING (dark) */
        .lr-evening{position:relative;overflow:hidden;background:var(--blue-dark);color:#fff}
        .lr-evening-glow{position:absolute;top:-20%;left:50%;transform:translateX(-50%);width:70%;height:100%;background:radial-gradient(closest-side,rgba(29,184,138,.13),transparent 70%);z-index:0}
        .lr-evening .lr-wrap{position:relative;z-index:1}
        .lr-evening .lr-num{color:var(--teal-light)}
        .lr-evening .lr-sechead h2{color:#fff}
        .lr-schedule{border-top:1px solid rgba(255,255,255,.16)}
        .lr-slot{display:grid;grid-template-columns:7.5rem 1fr;gap:22px;padding:15px 0;border-bottom:1px solid rgba(255,255,255,.1);transition:padding-left .25s}
        .lr-slot:hover{padding-left:10px}
        .lr-slot time{font-family:'DM Sans',sans-serif;font-size:14px;color:rgba(255,255,255,.6);padding-top:2px}
        .lr-slot-body p{margin:0;font-size:15.5px;color:rgba(255,255,255,.9);font-weight:400}
        .lr-slot-body small{display:block;font-family:'DM Sans',sans-serif;font-size:12.5px;color:rgba(255,255,255,.5);margin-top:4px;line-height:1.5}
        .lr-slot.is-mark{background:linear-gradient(90deg,rgba(29,184,138,.1),transparent);margin:0 -20px;padding-left:20px;padding-right:20px;border-radius:8px;border-bottom-color:rgba(45,212,160,.25)}
        .lr-slot.is-mark time{color:var(--teal-light)}
        .lr-slot.is-mark p{color:#fff}
        @media(max-width:640px){.lr-slot{grid-template-columns:5.4rem 1fr;gap:14px}}

        /* SEATS */
        .lr-seats{display:grid;grid-template-columns:1fr 1fr;gap:24px}
        .lr-seat{position:relative;background:#fff;border:1px solid var(--border);border-radius:18px;padding:38px 34px 34px;transition:transform .28s,box-shadow .28s,border-color .28s}
        .lr-seat:hover{transform:translateY(-5px);box-shadow:0 26px 56px rgba(26,79,160,.12)}
        .lr-seat.is-feat{border-color:transparent;box-shadow:0 20px 50px rgba(26,79,160,.12)}
        .lr-seat.is-feat::before{content:'';position:absolute;inset:0;border-radius:18px;padding:1.5px;background:var(--grad);-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none}
        .lr-seat-badge{position:absolute;top:-12px;right:28px;background:var(--grad);color:#fff;font-family:'DM Sans',sans-serif;font-size:10.5px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;padding:6px 14px;border-radius:20px;box-shadow:0 6px 16px rgba(29,184,138,.4)}
        .lr-seat h3{font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:var(--teal-dark);margin:0 0 14px}
        .lr-seat-price{font-family:'Cormorant Garamond',serif;font-size:clamp(36px,11vw,52px);font-weight:600;line-height:1;letter-spacing:-.02em;color:var(--blue-dark);margin-bottom:4px}
        .lr-seat.is-feat .lr-seat-price{background:var(--grad);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        .lr-seat-price sup{font-family:'DM Sans',sans-serif;font-size:12px;font-weight:400;color:var(--muted);margin-left:6px;top:-1.6em;-webkit-text-fill-color:var(--muted)}
        .lr-seat-cap{font-family:'DM Sans',sans-serif;font-size:12.5px;color:var(--muted);margin-bottom:22px}
        .lr-seat-list{list-style:none;margin:0 0 26px;padding:0}
        .lr-seat-list li{display:flex;gap:12px;align-items:flex-start;font-size:14px;color:var(--muted);line-height:1.5;font-weight:300;padding:11px 0;border-bottom:1px solid var(--border)}
        .lr-seat-list li:last-child{border-bottom:none}
        .lr-seat-check{width:20px;height:20px;border-radius:50%;background:rgba(29,184,138,.12);color:var(--teal-dark);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px}
        .lr-seat-check svg{width:13px;height:13px}
        .lr-seat-btn{width:100%;text-align:center;justify-content:center;padding:12px}

        /* APPLY */
        .lr-apply{display:grid;grid-template-columns:1fr 1.15fr;gap:clamp(28px,5vw,64px);align-items:start}
        .lr-contact-card{margin-top:30px;padding-top:26px;border-top:1px solid var(--border)}
        .lr-contact-card-label{font-family:'DM Sans',sans-serif;font-size:16px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--teal-dark);margin-bottom:16px}
        .lr-contact-tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px}
        .lr-contact-tag{font-family:'DM Sans',sans-serif;font-size:11px;font-weight:500;letter-spacing:.04em;text-transform:uppercase;color:var(--teal-dark);background:rgba(29,184,138,.1);border:1px solid rgba(29,184,138,.25);border-radius:20px;padding:5px 13px}
        .lr-contact-card-inner{position:relative;overflow:hidden;background:var(--blue-dark);border-radius:16px;padding:30px 32px}
        .lr-contact-card-inner::before{content:'';position:absolute;inset:0;border-radius:16px;padding:1.5px;background:var(--grad);-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none}
        .lr-cc-row{position:relative;z-index:1}
        .lr-cc-row+.lr-cc-row{margin-top:18px;padding-top:18px;border-top:1px solid rgba(255,255,255,.14)}
        .lr-cc-k{font-family:'DM Sans',sans-serif;font-size:11px;font-weight:500;letter-spacing:.06em;text-transform:uppercase;color:var(--teal-light);margin-bottom:6px}
        .lr-cc-v{font-size:18px;color:#fff;font-family:'Cormorant Garamond',serif;font-weight:500}
        .lr-cc-v a{color:#fff;text-decoration:none}
        .lr-cc-v a:hover{color:var(--teal-light)}
        .lr-form-card{position:relative;background:#fff;border:1px solid var(--border);border-radius:18px;padding:38px 34px 32px;box-shadow:0 26px 60px rgba(26,79,160,.1);overflow:hidden}
        .lr-form-accent{position:absolute;top:0;left:0;right:0;height:4px;background:var(--grad)}
        .lr-form-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        .lr-form-card .cf-input:focus,.lr-form-card .cf-select:focus,.lr-form-card .cf-textarea:focus{border-color:var(--teal);box-shadow:0 0 0 3px rgba(29,184,138,.13);outline:none}
        .lr-consent{display:flex;gap:11px;align-items:flex-start;font-family:'DM Sans',sans-serif;font-size:12.5px;color:var(--muted);line-height:1.55;font-weight:300;margin:6px 0 18px}
        .lr-consent input{width:auto;margin-top:2px;accent-color:var(--teal);flex-shrink:0}
        .lr-status{font-family:'DM Sans',sans-serif;font-size:13.5px;padding:13px 18px;border-radius:8px;margin-bottom:18px}
        .lr-status.is-ok{background:rgba(29,184,138,.1);border:1px solid rgba(29,184,138,.3);border-left:3px solid var(--teal);color:var(--blue-dark)}
        .lr-status.is-err{background:rgba(220,53,69,.1);border:1px solid rgba(220,53,69,.3);border-left:3px solid #DC3545;color:#DC3545}
        .lr-formnote{font-family:'DM Sans',sans-serif;font-size:12px;color:var(--muted);line-height:1.5;font-weight:300;margin:14px 0 0}

        /* COLOPHON */
        .lr-colophon{background:var(--blue-dark);color:rgba(255,255,255,.7);font-family:'DM Sans',sans-serif;padding:56px 0 60px}
        .lr-colo-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr;gap:32px;padding-bottom:30px;border-bottom:1px solid rgba(255,255,255,.14);font-size:13.5px;line-height:1.7}
        .lr-colo-grid strong{display:block;color:#fff;font-weight:600;font-family:'Cormorant Garamond',serif;font-size:18px;margin-bottom:8px}
        .lr-colophon a{color:var(--teal-light);text-decoration:none}
        .lr-disclaimer{margin:26px 0 0;padding-left:16px;border-left:2px solid var(--teal);max-width:62em;font-size:12.5px;color:rgba(255,255,255,.5);line-height:1.7}

        @media(max-width:900px){
          .lr-cover-grid{grid-template-columns:1fr;gap:38px}
          .lr-apply{grid-template-columns:1fr;gap:32px}
        }
        @media(max-width:820px){
          .lr-two,.lr-two-wide{grid-template-columns:1fr;gap:32px}
          .lr-seats{grid-template-columns:1fr}
          .lr-subnav-brand{display:none}
          .lr-subnav-links{margin-left:0}
          .lr-form-2{grid-template-columns:1fr}
          .lr-colo-grid{grid-template-columns:1fr;gap:22px}
        }
        @media(max-width:560px){.lr-cover{padding-top:128px}}
      `}</style>
    </>
  );
}
